
import {
  Address,
  SelectVM,
  SignUpBillingPrograms,
  SignUpRecordPrograms,
  SignUpServices,
  UserVM
} from '@/models'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Multiselect from 'vue-multiselect'
import { getInsurances } from '@/components/insurances/insurance.service'
import {
  deleteDoctorAddress,
  getDoctorAddresses,
  getDoctorInsurances,
  getGenders,
  getProgramOrServices
} from '@/components/doctors/doctors.service'
import moment from 'moment'
import { getSignUpResolve } from '@/components/admin/users/users.service'

@Component({
  name: 'DoctorForm',
  components: {
    Multiselect
  }
})
export default class DoctorForm extends Vue {
  @Prop() form: UserVM
  @Prop() editMode: boolean

  public insuranceList: SelectVM[] = []
  public selectedInsurances: SelectVM[] = []
  public selectedGender: string | null = null
  public genderList: SelectVM[] = [{ value: null, label: 'Select a gender' }]
  public formatted = ''
  public addressGroups: Address[] = [
    {
      addressID: null,
      city: null,
      confirmation: null,
      email: null,
      email1: null,
      location_name: null,
      phone: null,
      phone1: null,
      phone2: null,
      selected: null,
      state: null,
      street: null,
      zip: null,
      mobile: null
    }
  ]
  public billingSoftware: SignUpBillingPrograms[] = []
  public selectedSoftware: SignUpBillingPrograms[] = []
  public electronicRecord: SignUpRecordPrograms[] = []
  public otherServices: SignUpServices[] = []
  public selectedRecord: SignUpRecordPrograms[] = []
  public selectedServices: SignUpServices[] = []
  public showOtherBilling = false
  public showOtherProgram = false
  public doctorInsurances = ''
  @Watch('selectedInsurances', { deep: true })
  onSelectedInsurances() {
    this.syncInsurances()
  }

  public syncInsurances() {
    this.form.insurances = [
      ...new Set(
        (this.selectedInsurances || [])
          .map(i => String(i.value ?? '').trim())
          .filter(Boolean)
      )
    ].join(',')
  }

  @Watch('addressGroups', { deep: true })
  onAddressGroups(newVal: Address[]) {
    this.form.doctorAddresses = newVal.map(c => {
      ;(c.email1 = c.email), (c.phone = c.phone1)
      c.mobile = c.phone2
      return c
    })
    this.form.email1 = newVal[0].email
    this.form.phone = newVal[0].phone1
    this.form.mobile = newVal[0].phone2
    this.form.street = newVal[0].street
    this.form.city = newVal[0].city
    this.form.state = newVal[0].state
    this.form.zip = newVal[0].zip
  }
  @Watch('selectedGender')
  onSelectedGender(newVal) {
    this.form.sex = this.genderList.find(
      c => c.value == this.selectedGender
    )?.label
  }

  public async created() {
    this.form.role = 'Doctor'

    await Promise.all([
      this.fetchInsurances(),
      this.fetchGenders(),
      this.fetchPrograms()
    ])

    if (this.editMode) {
      this.setDoctorGender()
      this.setDoctorDateOfBirth()

      await Promise.all([
        this.setDoctorInsurances(),
        this.setDoctorAddresses(),
        this.setDoctorPrograms()
      ])
    }
  }
  public async setDoctorPrograms() {
    const data = await getProgramOrServices(this.form.documentID)
    const groupedData = data.reduce((acc, curr) => {
      if (!acc[curr.porS_Identifier]) {
        acc[curr.porS_Identifier] = []
      }
      acc[curr.porS_Identifier].push(curr.porS_Id)
      return acc
    }, {})

    const result = Object.keys(groupedData).map(key => ({
      text: key,
      value: groupedData[key]
    }))

    if (result.length === 0) return
    this.selectedRecord =
      result.filter(c => c.text == 'Record_Program')[0]?.value || []

    this.selectedServices =
      result.filter(c => c.text == 'Service_Required')[0]?.value || []

    this.selectedSoftware =
      result.filter(c => c.text == 'Billing_Program')[0]?.value || []

    this.form.otherBillingProgram = data.filter(
      c =>
        c.porS_Identifier == 'Billing_Program' &&
        c.programorServiceName2 == 'Other'
    )[0]?.programorServiceName
    this.form.otherRecordProgram = data.filter(
      c =>
        c.porS_Identifier == 'Record_Program' &&
        c.programorServiceName2 == 'Other'
    )[0]?.programorServiceName
  }
  public setDoctorConfirmation() {
    this.addressGroups.forEach(g => (g.confirmation = g.email))
  }
  public setDoctorDateOfBirth() {
    this.formatted = moment(this.form.dob).format('MM/DD/YYYY')
  }
  public setDoctorGender() {
    this.selectedGender = this.genderList.find(
      c => c.label == this.form.sex
    )?.value
  }
  public async setDoctorAddresses() {
    const rmtUserId = this.form.rmT_UserId
    if (rmtUserId == null || rmtUserId === undefined) return

    const doctorAddresses = await getDoctorAddresses(rmtUserId.toString())

    this.addressGroups =
      doctorAddresses.length > 0 ? doctorAddresses : this.addressGroups
    this.setDoctorConfirmation()
  }
  public async setDoctorInsurances() {
    const rmtUserId = this.form.rmT_UserId
    if (rmtUserId == null || rmtUserId === undefined) return

    const doctorInsurances = await getDoctorInsurances(rmtUserId)
    this.selectedInsurances = this.insuranceList.filter(i =>
      doctorInsurances.some(di => di.insuranceID.toString() == i.value)
    )
    this.doctorInsurances = this.selectedInsurances.map(i => i.value).join(',')
  }
  public async fetchInsurances() {
    const response = await getInsurances(1, 1500)
    this.insuranceList = response.data.map(i => ({
      label: i.insuranceName,
      value: i.insuranceID.toString(),
      _isDisabled: false
    }))
  }
  public async fetchGenders() {
    const response = await getGenders()
    this.genderList.push(
      ...response.map(g => ({
        label: g.label,
        value: g.value.toString(),
        _isDisabled: false
      }))
    )
  }
  public async fetchPrograms() {
    const result = await getSignUpResolve()
    this.billingSoftware = result.billingPrograms
    this.electronicRecord = result.recordPrograms
    this.otherServices = result.services
  }

  public onBillingSoftwareInput(e) {
    this.$set(this.form, 'rmtBillingProgram', e.join(','))
    const id = this.billingSoftware.filter(
      c => c.billingProgramName == 'Other'
    )[0].billingProgramID

    this.showOtherBilling = e.includes(id)
  }
  public onRecordSoftwareInput(e) {
    this.$set(this.form, 'rmtRecordProgram', e.join(','))
    const id = this.electronicRecord.filter(
      c => c.recordProgramName == 'Other'
    )[0].recordProgramID

    this.showOtherProgram = e.includes(id)
  }
  public onOtherServicesInput(e) {
    this.$set(this.form, 'rmtServices', e.join(','))
  }

  public removeAddressGroup(address, index: number) {
    console.log(address)
    this.$swal
      .fire({
        title: 'Are you sure?',
        text: `This will delete this address permanently`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Confirm'
      })
      .then(result => {
        if (result.isConfirmed) {
          if (address.addressID) {
            const obj = {
              doctorID: address.doctorID,
              addressID: address.addressID
            }
            deleteDoctorAddress(obj)
          }
          this.addressGroups.splice(index, 1)
        }
      })
  }
  public addMore() {
    const obj = {} as Address
    this.addressGroups.push(obj)
  }
  public selectAllInsurances() {
    const all = (this.insuranceList || []).map(i => ({ ...i }))
    this.selectedInsurances = []
    this.$nextTick(() => {
      this.selectedInsurances = all
    })
  }

  public onDOBContext(ctx) {
    this.formatted = moment(ctx).format('MM/DD/YYYY')
  }
  public onDOBInput(ctx) {
    const a = moment(ctx).toDate()
    if (a.toString() != 'Invalid Date') this.form.dob = a
  }
  public onRemove(option) {
    this.syncInsurances()
    if (this.doctorInsurances.split(',').includes(String(option.value))) {
      this.form._showInsuranceAgreementAlert = true
    }
  }
  public getLength(value) {
    return value ? value.toString().trim().length : 0
  }
}
